/**
 * onboardingClient.ts — API client for the F-351 HA orchestration onboarding
 * endpoints (controller readiness + commissioning jobs).
 *
 * All endpoints are under `/api/v1/onboarding/`. The gateway base URL is
 * read from the same env var that `discoveryClient.ts` / `gateway-client.ts`
 * use so every client resolves to the same host in dev / tests / production.
 *
 * The functions below are deliberately fetch-based (no generated codegen
 * layer) to match the pattern used by `discoveryClient.ts` until the F-351
 * OpenAPI schema is committed and codegen is re-run.
 */

// Same-origin by default (issue #532): an empty/unset `VITE_GATEWAY_BASE_URL`
// yields a RELATIVE base so calls resolve against the page origin. An absolute
// `http://localhost:8200` default made these cross-origin on the kiosk (page
// served at 127.0.0.1:8200), which WPEWebKit CORS-blocks — so the controller
// probe (`getControllers`) failed and the Matter/Zigbee steps silently
// self-skipped. See `gateway-client.ts`.
const GATEWAY_BASE_URL: string = import.meta.env.VITE_GATEWAY_BASE_URL || "";

const API_BASE = `${GATEWAY_BASE_URL}/api/v1`;

// ── Domain types ───────────────────────────────────────────────────────────

export interface Controller {
  kind: "matter" | "zigbee";
  available: boolean;
  integration_loaded: boolean;
  integration_state: string | null;
  radio_present: boolean | null;
  detail: string;
  last_checked_at: string | null;
}

export interface ControllersStatus {
  controllers: Controller[];
  ha_reachable: boolean;
  checked_at: string | null;
}

export interface CommissioningJob {
  job_id: string;
  kind: "matter" | "zigbee";
  state: string;
  progress: number;
  code_kind: string | null;
  device_ref: string | null;
  error: string | null;
  events: string[];
  started_at: string | null;
  updated_at: string | null;
}

// ── API functions ──────────────────────────────────────────────────────────

/**
 * Fetch the readiness status of the Matter + Zigbee controllers.
 * GET /api/v1/onboarding/controllers
 */
export async function getControllers(): Promise<ControllersStatus> {
  const res = await fetch(`${API_BASE}/onboarding/controllers`);
  if (!res.ok) {
    throw new Error(`Failed to get controllers: HTTP ${res.status}`);
  }
  return res.json() as Promise<ControllersStatus>;
}

/**
 * Start a Matter commissioning job from a pairing code.
 * POST /api/v1/onboarding/matter/commission
 *
 * `codeKind` distinguishes a scanned QR payload from a manually-typed
 * 11-digit code; when omitted the gateway infers it (sent as `null`).
 */
export async function commissionMatter(
  code: string,
  codeKind?: "qr" | "manual",
): Promise<CommissioningJob> {
  const res = await fetch(`${API_BASE}/onboarding/matter/commission`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code, code_kind: codeKind ?? null }),
  });
  if (!res.ok) {
    throw new Error(`Failed to commission Matter device: HTTP ${res.status}`);
  }
  return res.json() as Promise<CommissioningJob>;
}

/**
 * Open the Zigbee network to permit a new device to join (pairing).
 * POST /api/v1/onboarding/zigbee/permit
 *
 * `durationS` controls how long the network stays open (default 60s). The
 * optional `qrCode` / `installCode` / `srcIeee` fields support
 * install-code-based joining or routing through a specific router; when
 * omitted they are sent as `null`.
 */
export async function permitZigbee(opts: {
  durationS?: number;
  qrCode?: string;
  installCode?: string;
  srcIeee?: string;
}): Promise<CommissioningJob> {
  const res = await fetch(`${API_BASE}/onboarding/zigbee/permit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      duration_s: opts.durationS ?? 60,
      qr_code: opts.qrCode ?? null,
      install_code: opts.installCode ?? null,
      src_ieee: opts.srcIeee ?? null,
    }),
  });
  if (!res.ok) {
    throw new Error(`Failed to permit Zigbee join: HTTP ${res.status}`);
  }
  return res.json() as Promise<CommissioningJob>;
}

/**
 * Poll the status of an in-progress commissioning job.
 * GET /api/v1/onboarding/jobs/{job_id}
 */
export async function getJob(jobId: string): Promise<CommissioningJob> {
  const res = await fetch(`${API_BASE}/onboarding/jobs/${jobId}`);
  if (!res.ok) {
    throw new Error(`Failed to get job: HTTP ${res.status}`);
  }
  return res.json() as Promise<CommissioningJob>;
}

/**
 * Cancel an in-progress commissioning job.
 * DELETE /api/v1/onboarding/jobs/{job_id}
 */
export async function cancelJob(jobId: string): Promise<CommissioningJob> {
  const res = await fetch(`${API_BASE}/onboarding/jobs/${jobId}`, {
    method: "DELETE",
  });
  if (!res.ok) {
    throw new Error(`Failed to cancel job: HTTP ${res.status}`);
  }
  return res.json() as Promise<CommissioningJob>;
}
